import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect } from 'react';
import { RepositoryProvider } from '@/lib/RepositoryContext';
import { db } from '@/lib/db';
import BottomNav from '@/components/BottomNav';
import Onboarding from '@/components/Onboarding';
import HomePage from '@/pages/HomePage';
import WorkoutPage from '@/pages/WorkoutPage';
import HistoryPage from '@/pages/HistoryPage';
import SettingsPage from '@/pages/SettingsPage';
import ExerciseLibraryPage from '@/pages/ExerciseLibraryPage';

function AppShell() {
  const settings = useLiveQuery(() => db.settings.get('singleton'), []);
  // null = loading, undefined = not yet in DB, object = loaded
  const onboardingDone = settings?.onboardingComplete ?? false;
  const isReady = settings !== undefined;

  // Apply theme to document root
  useEffect(() => {
    const theme = settings?.theme ?? 'dark';
    document.documentElement.dataset.theme = theme;
  }, [settings?.theme]);

  // Re-arm notification reminder on app load
  useEffect(() => {
    if (!settings) return;
    import('@/lib/notifications').then(({ rescheduleIfEnabled }) => {
      rescheduleIfEnabled(
        settings.notificationsEnabled ?? false,
        settings.notificationTime ?? '08:00',
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isReady) return null; // RepositoryProvider shows loading screen

  if (!onboardingDone) {
    // Force re-render after save by relying on useLiveQuery reactivity
    return <Onboarding onComplete={() => {}} />;
  }

  return (
    <BrowserRouter>
      {/* Full-height layout column */}
      <div className="relative flex flex-col h-[100dvh] bg-surface overflow-hidden">

        {/* Scrollable page area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden no-scrollbar scroll-smooth-ios">
          <Routes>
            <Route path="/"          element={<HomePage />} />
            <Route path="/workout"   element={<WorkoutPage />} />
            <Route path="/history"   element={<HistoryPage />} />
            <Route path="/library"   element={<ExerciseLibraryPage />} />
            <Route path="/settings"  element={<SettingsPage />} />
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/* Persistent bottom navigation */}
        <BottomNav />
      </div>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <RepositoryProvider>
      <AppShell />
    </RepositoryProvider>
  );
}
